import { createSlice } from '@reduxjs/toolkit';
import { get, post, put, del } from 'api/fetch';
import { PERSON_DETAILS_TYPE, PLANS } from 'products/nomad_insurance/constants';

// ================== Reducers ================== //

const slice = createSlice({
  name: 'nomad_insurance/application',
  initialState: {
    isLoading: false,
    error: null,
    plans: null,
    offer: null,
    applicants: [],
    groupMembers: [],
    planholder: null,
    loadingGroupMemberID: null
  },
  reducers: {
    // fetchPlans
    fetchPlansRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchPlansSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.plans = payload;
    },
    fetchPlansFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    // addApplicant
    addApplicantRequest: (state) => {
      state.isLoading = true;
      state.error = null;
      state.loadingGroupMemberID = 0;
    },
    addApplicantSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.offer = payload;
      state.loadingGroupMemberID = null;
      state.applicants = payload.applicants.map((applicant) => applicant.userId);
      state.groupMembers = payload.applicants.filter(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.GROUP_MEMBER
      );
    },
    addApplicantFailure: (state, { payload }) => {
      state.isLoading = false;
      state.loadingGroupMemberID = null;
      state.error = payload;
    },
    // updateApplicant
    updateApplicantRequest: (state, { payload }) => {
      state.isLoading = true;
      state.error = null;
      state.loadingGroupMemberID = payload;
    },
    updateApplicantSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.loadingGroupMemberID = null;
      state.offer = payload;
      state.applicants = payload.applicants.map((applicant) => applicant.userId);
      state.planholder = payload.applicants.find(
        (applicant) => applicant.personDetailsType === 'PolicyHolder'
      );
      state.groupMembers = payload.applicants.filter(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.GROUP_MEMBER
      );
    },
    updateApplicantFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.loadingGroupMemberID = null;
    },
    // removeApplicant
    removeApplicantRequest: (state, { payload }) => {
      state.isLoading = true;
      state.error = null;
      state.loadingGroupMemberID = payload;
    },
    removeApplicantSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.offer = payload;
      state.loadingGroupMemberID = null;
      state.applicants = payload.applicants.map((applicant) => applicant.userId);
      state.planholder = payload.applicants.find(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.POLICY_HOLDER
      );
      state.groupMembers = payload.applicants.filter(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.GROUP_MEMBER
      );
    },
    removeApplicantFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.loadingGroupMemberID = null;
    },
    // generateApplicationOffer
    generateApplicationOfferRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    generateApplicationOfferSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.offer = payload;
      state.applicants = payload.applicants.map((applicant) => applicant.userId);
      state.planholder = payload.applicants.find(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.POLICY_HOLDER
      );
      state.groupMembers = payload.applicants.filter(
        (applicant) => applicant.personDetailsType === PERSON_DETAILS_TYPE.GROUP_MEMBER
      );
    },
    generateApplicationOfferFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.offer = null;
    }
  }
});

export default slice.reducer;

// ================== Actions ================== //

const { actions } = slice;

export const fetchPlans = () => async (dispatch) => {
  dispatch(actions.fetchPlansRequest());

  try {
    const response = await get(`/insurance-offers/v1`, {
      isNIOrchestrator: true
    });

    dispatch(actions.fetchPlansSuccess(response));
  } catch (error) {
    dispatch(actions.fetchPlansFailure(error));
  }
};

export const generateApplicationOffer =
  (
    policyHolderUserId,
    applicants,
    addons,
    startDate,
    endDate,
    beneficiary,
    phoneNumber,
    phoneCountryCode,
    gender
  ) =>
  async (dispatch) => {
    dispatch(actions.generateApplicationOfferRequest());

    try {
      const body = {
        applicants: [
          {
            userId: policyHolderUserId,
            startDate,
            endDate,
            planIds: [
              PLANS.STANDARD_NOMAD_INSURANCE.id,
              ...addons.map((addon) => addon.id)
            ],
            beneficiary,
            phoneNumber,
            phoneCountryCode,
            gender: gender?.toUpperCase()
          },
          ...applicants
            .filter((applicantId) => applicantId !== policyHolderUserId)
            .map((applicantId) => ({
              userId: applicantId,
              startDate,
              endDate,
              planIds: [
                PLANS.STANDARD_NOMAD_INSURANCE.id,
                ...addons.map((addon) => addon.id)
              ]
            }))
        ]
      };

      const response = await post(`/applications/v1`, body, {
        isNIOrchestrator: true
      });

      dispatch(actions.generateApplicationOfferSuccess(response));
    } catch (error) {
      dispatch(actions.generateApplicationOfferFailure(error));
    }
  };

const buildUpsertApplicatDto = (applicantData, startDate, endDate, addons) => {
  const startDateFormatted = startDate.slice(0, 10);
  const endDateFormatted = endDate ? endDate.slice(0, 10) : null;
  return {
    email: applicantData.email,
    firstName: applicantData.firstName,
    lastName: applicantData.lastName,
    gender: applicantData.gender.toUpperCase(),
    dateOfBirth: applicantData.dateOfBirth,
    citizenship: applicantData.citizenship,
    homeCountry: applicantData.homeCountry,
    startDate: startDateFormatted,
    endDate: endDateFormatted,
    planIds: [PLANS.STANDARD_NOMAD_INSURANCE.id, ...addons.map((addon) => addon.id)]
  };
};

export const addApplicant =
  (applicationId, applicantData) => async (dispatch, getState) => {
    dispatch(actions.addApplicantRequest());
    const { addons, startDate, endDate } =
      getState().nomadInsurance.purchaseInsurance.coverage;

    try {
      const response = await post(
        `/applications/v1/${applicationId}/applicants`,
        buildUpsertApplicatDto(applicantData, startDate, endDate, addons),
        {
          isNIOrchestrator: true
        }
      );

      dispatch(actions.addApplicantSuccess(response));
    } catch (error) {
      dispatch(actions.addApplicantFailure(error));
    }
  };

export const updateApplicant =
  (applicationId, applicantId, applicantData) => async (dispatch, getState) => {
    dispatch(actions.updateApplicantRequest(applicantId));
    const { addons, startDate, endDate } =
      getState().nomadInsurance.purchaseInsurance.coverage;

    try {
      const response = await put(
        `/applications/v1/${applicationId}/applicants/${applicantId}`,
        buildUpsertApplicatDto(applicantData, startDate, endDate, addons),
        {
          isNIOrchestrator: true
        }
      );

      dispatch(actions.updateApplicantSuccess(response));
    } catch (error) {
      dispatch(actions.updateApplicantFailure(error));
    }
  };

export const removeApplicant = (applicationId, applicantId) => async (dispatch) => {
  dispatch(actions.removeApplicantRequest(applicantId));

  try {
    const response = await del(
      `/applications/v1/${applicationId}/applicants/${applicantId}`,
      null,
      {
        isNIOrchestrator: true
      }
    );

    dispatch(actions.removeApplicantSuccess(response));
  } catch (error) {
    dispatch(actions.removeApplicantFailure(error));
  }
};
